import { AnimatePresence, motion } from 'framer-motion'
import { Calendar as CalendarIcon, Filter, X } from 'lucide-react'
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { useAuth } from '../../../Contexts/AuthContext'
import type { SmartSearchFilters } from '../../../Contexts/SmartSearchContext'
import {
  listSearchCreators,
  type GlobalSearchKind,
  type RasterSource,
  type RasterType,
  type SearchCreator,
} from '../../../api/globalSearchApi'
import { useTheme } from '../../../theme/hooks'
import Calendar, { toDisplayFR } from '../../shared/Calendar'

const TYPE_OPTIONS: ReadonlyArray<{ value: GlobalSearchKind; label: string }> =
  [
    { value: 'message', label: 'Messages' },
    { value: 'vector_layer', label: 'Couches SIG' },
    { value: 'raster', label: 'Rasters' },
    { value: 'vector_feature', label: 'Objets' },
  ]

const RASTER_SOURCE_OPTIONS: ReadonlyArray<{
  value: RasterSource
  label: string
}> = [
  { value: 'lidar_pipeline', label: 'LiDAR (neige)' },
  { value: 'gis_upload', label: 'Upload GIS' },
]

const RASTER_TYPE_OPTIONS: ReadonlyArray<{
  value: RasterType
  label: string
}> = [
  { value: 'snow_depth', label: 'Hauteur de neige' },
  { value: 'dtm', label: 'MNT' },
  { value: 'dsm', label: 'MNS' },
  { value: 'ortho_rgb', label: 'Ortho RGB' },
  { value: 'ortho_ir', label: 'Ortho IR' },
  { value: 'custom', label: 'Custom' },
]

type CommonProps = {
  filters: SmartSearchFilters
  onChange: (
    next:
      | SmartSearchFilters
      | ((prev: SmartSearchFilters) => SmartSearchFilters),
  ) => void
  onReset: () => void
  active: boolean
}

// ISO datetime <-> YYYY-MM-DD (Calendar's preferred format)
const isoToYmd = (iso: string | null): string => (iso ? iso.slice(0, 10) : '')

// start-of-day UTC for "after"; end-of-day UTC for "before" so that the
// selected day is inclusive on both ends.
const ymdToIsoStart = (ymd: string): string | null =>
  ymd ? new Date(ymd + 'T00:00:00.000Z').toISOString() : null
const ymdToIsoEnd = (ymd: string): string | null =>
  ymd ? new Date(ymd + 'T23:59:59.999Z').toISOString() : null

// shared header (trigger button + reset shortcut), used by both layouts.
type TriggerProps = CommonProps & {
  open: boolean
  onOpenChange: (open: boolean) => void
}

function SmartSearchFiltersTriggerBase({
  open,
  onOpenChange,
  onReset,
  active,
}: TriggerProps) {
  const { theme } = useTheme()
  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        className="inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-[11px] font-semibold transition-colors"
        style={{
          backgroundColor:
            active || open ? theme.section.bgSubtle : 'transparent',
          color: active || open ? theme.text.primary : theme.text.secondary,
          borderColor:
            active || open
              ? theme.section.borderStrong ?? theme.section.border
              : theme.section.border,
        }}
        onMouseDown={e => e.preventDefault()}
        onClick={() => onOpenChange(!open)}
        aria-expanded={open}
      >
        <Filter size={13} strokeWidth={2.25} aria-hidden />
        Filtres
        {active ? (
          <span
            className="ml-0.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[9px] font-bold"
            style={{
              backgroundColor: theme.text.link,
              color: theme.section.bg,
            }}
          >
            !
          </span>
        ) : null}
      </button>
      {active ? (
        <button
          type="button"
          className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[11px] font-medium transition-colors"
          style={{ color: theme.text.secondary }}
          onMouseDown={e => e.preventDefault()}
          onClick={onReset}
          title="Réinitialiser les filtres"
        >
          <X size={12} aria-hidden /> Réinitialiser
        </button>
      ) : null}
    </div>
  )
}

export const SmartSearchFiltersTrigger = memo(SmartSearchFiltersTriggerBase)

// Pure body content, animation-free. Used by the side drawer.
type BodyProps = CommonProps & {
  // when true (desktop split rail), skip the inner card chrome.
  embedded?: boolean
}

function SmartSearchFiltersBodyBase({
  filters,
  onChange,
  embedded = false,
}: BodyProps) {
  const { theme, isDark } = useTheme()
  const { hasModule } = useAuth()

  // LiDAR raster source is gated behind the snow_depth_lidar module; the
  // socle GIS upload source stays available regardless.
  const rasterSourceOptions = useMemo(
    () =>
      RASTER_SOURCE_OPTIONS.filter(
        opt =>
          opt.value !== 'lidar_pipeline' || hasModule('snow_depth_lidar'),
      ),
    [hasModule],
  )

  const [creators, setCreators] = useState<SearchCreator[]>([])
  const [dateOpen, setDateOpen] = useState(false)
  const dateAnchorRef = useRef<HTMLDivElement>(null)
  const datePopupRef = useRef<HTMLDivElement>(null)
  const [datePopupPos, setDatePopupPos] = useState<{
    top: number
    left: number
  } | null>(null)

  const updateDatePopupPos = useCallback(() => {
    const el = dateAnchorRef.current
    if (!el) return
    const r = el.getBoundingClientRect()
    const PADDING = 8
    const POPUP_W = 320
    let left = r.left
    if (left + POPUP_W + PADDING > window.innerWidth) {
      left = Math.max(PADDING, window.innerWidth - POPUP_W - PADDING)
    }
    setDatePopupPos({ top: r.bottom + 8, left: Math.max(PADDING, left) })
  }, [])

  useEffect(() => {
    if (!dateOpen) {
      setDatePopupPos(null)
      return
    }
    updateDatePopupPos()
    const onPointerDown = (e: MouseEvent) => {
      const t = e.target as Node
      if (dateAnchorRef.current?.contains(t)) return
      if (datePopupRef.current?.contains(t)) return
      setDateOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDateOpen(false)
    }
    window.addEventListener('mousedown', onPointerDown)
    window.addEventListener('keydown', onKey)
    window.addEventListener('resize', updateDatePopupPos)
    window.addEventListener('scroll', updateDatePopupPos, true)
    return () => {
      window.removeEventListener('mousedown', onPointerDown)
      window.removeEventListener('keydown', onKey)
      window.removeEventListener('resize', updateDatePopupPos)
      window.removeEventListener('scroll', updateDatePopupPos, true)
    }
  }, [dateOpen, updateDatePopupPos])

  // only users that actually authored at least one accessible object.
  useEffect(() => {
    if (creators.length > 0) return
    let cancelled = false
    void (async () => {
      try {
        const rows = await listSearchCreators()
        if (!cancelled) setCreators(rows)
      } catch (err) {
        console.error('[smart-search] Failed to load creators', err)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [creators.length])

  const toggleType = useCallback(
    (t: GlobalSearchKind) => {
      onChange(prev => {
        const has = prev.types.includes(t)
        const nextTypes = has
          ? prev.types.filter(x => x !== t)
          : [...prev.types, t]
        return { ...prev, types: nextTypes }
      })
    },
    [onChange],
  )

  const toggleRasterSource = useCallback(
    (s: RasterSource) => {
      onChange(prev => {
        const has = prev.rasterSources.includes(s)
        return {
          ...prev,
          rasterSources: has
            ? prev.rasterSources.filter(x => x !== s)
            : [...prev.rasterSources, s],
        }
      })
    },
    [onChange],
  )

  const toggleRasterType = useCallback(
    (t: RasterType) => {
      onChange(prev => {
        const has = prev.rasterTypes.includes(t)
        return {
          ...prev,
          rasterTypes: has
            ? prev.rasterTypes.filter(x => x !== t)
            : [...prev.rasterTypes, t],
        }
      })
    },
    [onChange],
  )

  const setCreatorId = useCallback(
    (id: number | null) => {
      onChange(prev => ({ ...prev, creatorId: id }))
    },
    [onChange],
  )

  const setDateRange = useCallback(
    (start: string, end: string) => {
      onChange(prev => ({
        ...prev,
        createdAfter: ymdToIsoStart(start),
        createdBefore: ymdToIsoEnd(end),
      }))
    },
    [onChange],
  )

  const clearDateRange = useCallback(() => {
    onChange(prev => ({ ...prev, createdAfter: null, createdBefore: null }))
  }, [onChange])

  const dateRangeLabel = useMemo(() => {
    const a = isoToYmd(filters.createdAfter)
    const b = isoToYmd(filters.createdBefore)
    if (!a && !b) return 'Toutes les dates'
    if (a && b) return `${toDisplayFR(a)} → ${toDisplayFR(b)}`
    if (a) return `À partir du ${toDisplayFR(a)}`
    return `Jusqu'au ${toDisplayFR(b)}`
  }, [filters.createdAfter, filters.createdBefore])

  const dateRangeForCalendar = useMemo(() => {
    const start = isoToYmd(filters.createdAfter)
    const end = isoToYmd(filters.createdBefore)
    if (!start && !end) return undefined
    return { start, end }
  }, [filters.createdAfter, filters.createdBefore])

  const showRasterSubFilters = filters.types.includes('raster')

  const chip = useCallback(
    (selected: boolean): React.CSSProperties => ({
      backgroundColor: selected ? theme.section.bgSubtle : 'transparent',
      color: selected ? theme.text.primary : theme.text.secondary,
      borderColor: selected
        ? theme.section.borderStrong ?? theme.section.border
        : theme.section.border,
    }),
    [theme],
  )

  const labelClass = 'mb-1.5 text-[10px] font-semibold uppercase'
  const labelStyle: React.CSSProperties = { color: theme.text.tertiary }

  const inputStyle = useMemo(
    (): React.CSSProperties => ({
      backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : theme.section.bg,
      color: theme.text.primary,
      borderColor: theme.section.border,
    }),
    [theme, isDark],
  )

  const content = (
    <>
      <div>
        <div className={labelClass} style={labelStyle}>
          Type d'objet
        </div>
        <div className="flex flex-wrap gap-1.5">
          {TYPE_OPTIONS.map(opt => {
            const sel = filters.types.includes(opt.value)
            return (
              <button
                key={opt.value}
                type="button"
                className="rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors"
                style={chip(sel)}
                onMouseDown={e => e.preventDefault()}
                onClick={() => toggleType(opt.value)}
              >
                {opt.label}
              </button>
            )
          })}
        </div>
      </div>

      <div className="mt-3">
        <div className={labelClass} style={labelStyle}>
          Plage de dates
        </div>
        <div ref={dateAnchorRef} className="relative">
          <button
            type="button"
            className="flex w-full items-center gap-2 rounded-lg border px-2 py-1.5 text-left text-[12px] outline-none transition-colors"
            style={inputStyle}
            onMouseDown={e => e.preventDefault()}
            onClick={() => setDateOpen(o => !o)}
          >
            <CalendarIcon
              size={14}
              strokeWidth={2.25}
              aria-hidden
              style={{ color: theme.text.secondary }}
            />
            <span className="flex-1 truncate">{dateRangeLabel}</span>
            {filters.createdAfter || filters.createdBefore ? (
              <span
                role="button"
                tabIndex={0}
                aria-label="Effacer la plage de dates"
                className="rounded p-0.5 transition-opacity hover:opacity-70"
                style={{ color: theme.text.tertiary }}
                onMouseDown={e => {
                  e.preventDefault()
                  e.stopPropagation()
                }}
                onClick={e => {
                  e.stopPropagation()
                  clearDateRange()
                }}
              >
                <X size={12} aria-hidden />
              </span>
            ) : null}
          </button>

          {dateOpen && datePopupPos
            ? createPortal(
                <div
                  ref={datePopupRef}
                  className="fixed z-[100000] w-[320px] max-w-[calc(100vw-16px)] rounded-xl border shadow-xl"
                  style={{
                    top: datePopupPos.top,
                    left: datePopupPos.left,
                    borderColor: theme.section.border,
                    backgroundColor: theme.section.bg,
                    transformOrigin: 'top left',
                  }}
                >
                  <Calendar
                    mode="range"
                    value={dateRangeForCalendar}
                    focusField={
                      filters.createdAfter && !filters.createdBefore
                        ? 'end'
                        : 'start'
                    }
                    onChange={value => {
                      if (
                        typeof value === 'object' &&
                        value !== null &&
                        'start' in value &&
                        'end' in value
                      ) {
                        setDateRange(value.start, value.end)
                        if (value.start && value.end) setDateOpen(false)
                      }
                    }}
                  />
                </div>,
                document.body,
              )
            : null}
        </div>
      </div>

      <div className="mt-3">
        <div className={labelClass} style={labelStyle}>
          Créateur
        </div>
        <select
          className="w-full rounded-lg border px-2 py-1.5 text-[12px] outline-none focus:ring-2"
          style={inputStyle}
          value={filters.creatorId ?? ''}
          onChange={e => {
            const v = e.target.value
            setCreatorId(v === '' ? null : Number(v))
          }}
        >
          <option value="">Tous</option>
          {creators.map(c => (
            <option key={c.id} value={c.id}>
              {c.username}
            </option>
          ))}
        </select>
        {creators.length === 0 ? (
          <p
            className="mt-1 text-[10px]"
            style={{ color: theme.text.tertiary }}
          >
            Aucun créateur identifié dans vos données accessibles.
          </p>
        ) : null}
      </div>

      {showRasterSubFilters ? (
        <>
          <div className="mt-3">
            <div className={labelClass} style={labelStyle}>
              Source du raster
            </div>
            <div className="flex flex-wrap gap-1.5">
              {rasterSourceOptions.map(opt => {
                const sel = filters.rasterSources.includes(opt.value)
                return (
                  <button
                    key={opt.value}
                    type="button"
                    className="rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors"
                    style={chip(sel)}
                    onMouseDown={e => e.preventDefault()}
                    onClick={() => toggleRasterSource(opt.value)}
                  >
                    {opt.label}
                  </button>
                )
              })}
            </div>
          </div>
          <div className="mt-3">
            <div className={labelClass} style={labelStyle}>
              Type de raster
            </div>
            <div className="flex flex-wrap gap-1.5">
              {RASTER_TYPE_OPTIONS.map(opt => {
                const sel = filters.rasterTypes.includes(opt.value)
                return (
                  <button
                    key={opt.value}
                    type="button"
                    className="rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors"
                    style={chip(sel)}
                    onMouseDown={e => e.preventDefault()}
                    onClick={() => toggleRasterType(opt.value)}
                  >
                    {opt.label}
                  </button>
                )
              })}
            </div>
          </div>
        </>
      ) : null}
    </>
  )

  if (embedded) return content

  return (
    <div
      className="rounded-xl border p-3"
      style={{
        borderColor: theme.section.border,
        backgroundColor: isDark
          ? 'rgba(255,255,255,0.02)'
          : theme.section.bgSubtle,
      }}
    >
      {content}
    </div>
  )
}

export const SmartSearchFiltersBody = memo(SmartSearchFiltersBodyBase)

// Backwards-compatible default export: trigger + animated inline body (used
// only by the mobile layout now).
type Props = CommonProps & {
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

function SmartSearchFiltersPanelBase({
  filters,
  onChange,
  onReset,
  active,
  open: openProp,
  onOpenChange,
}: Props) {
  const [openInternal, setOpenInternal] = useState(false)
  const open = openProp ?? openInternal
  const setOpen = useCallback(
    (next: boolean) => {
      if (onOpenChange) onOpenChange(next)
      else setOpenInternal(next)
    },
    [onOpenChange],
  )

  return (
    <div className="mb-3">
      <SmartSearchFiltersTrigger
        filters={filters}
        onChange={onChange}
        onReset={onReset}
        active={active}
        open={open}
        onOpenChange={setOpen}
      />
      <AnimatePresence initial={false}>
        {open ? (
          <motion.div
            key="filters-panel"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <div className="mt-2">
              <SmartSearchFiltersBody
                filters={filters}
                onChange={onChange}
                onReset={onReset}
                active={active}
              />
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  )
}

export default memo(SmartSearchFiltersPanelBase)
